import type {
  MergeCountsDto,
  MergeDecisionDto,
  MergeSection,
} from '../api/mergeResolution.types';

import type { CellFact, CellKey, LoadedPage } from './types';
import { cellKey } from './cellKey';
import { valuesEqual } from './serverEqual';

/**
 * What the counts need to know about each cell of the rows in `pages` that can
 * take a decision. A NoDecisionNeeded cell asks no question and is never sent
 * a decision, so it is not kept. An UNCHANGED cell can take a custom value
 * only (`legality.ts`), which is an override of Mine.
 */
export function cellFactsOf(
  pages: Partial<Record<MergeSection, LoadedPage>>
): Record<CellKey, CellFact> {
  const facts: Record<CellKey, CellFact> = {};
  Object.values(pages).forEach((page) => {
    page?.rows.forEach((row) => {
      row.Periods.forEach((period) => {
        if (period.NoDecisionNeeded) {
          return;
        }
        const key = cellKey(row.Input_RowID, period.DateID);
        if (period.Class === 'CONFLICT') {
          facts[key] = {
            conflict: true,
            inputName:
              row.Input_Name === undefined ? undefined : (row.Input_Name ?? ''),
          };
          return;
        }
        facts[key] = {
          conflict: false,
          inputName:
            row.Input_Name === undefined ? undefined : (row.Input_Name ?? ''),
          mine: period.Mine,
          theirs: period.Theirs,
          autoTheirs: period.Class === 'THEIRS_ONLY',
        };
      });
    });
  });
  return facts;
}

/** The value a decision writes: Mine, Theirs, empty for CLEAR, or the typed value. */
function decidedValue(
  fact: CellFact,
  decision: MergeDecisionDto
): string | null | undefined {
  switch (decision.choice) {
    case 'MINE':
      return fact.mine;
    case 'THEIRS':
      return fact.theirs;
    case 'CLEAR':
      return null;
    default:
      return decision.custom_value ?? null;
  }
}

/**
 * Whether a decision on a cell that is not a CONFLICT counts as an override.
 * The API's rule (`engine.counts`): the decided value differs, by the diff's own
 * equality, from the auto value. "Take Mine" on a MINE_ONLY cell is no override.
 */
function isOverride(fact: CellFact, decision: MergeDecisionDto): boolean {
  const auto = fact.autoTheirs ? fact.theirs : fact.mine;
  return !valuesEqual(decidedValue(fact, decision), auto);
}

/**
 * Estimate counts by applying local decisions to server counts, over the
 * rows of `pages` only. See `estimateCountsFromFacts` for the counts of every
 * decision, whichever page its row was on.
 */
export function estimateCounts(
  server: MergeCountsDto,
  serverDecisionKeys: CellKey[],
  decisions: Record<CellKey, MergeDecisionDto>,
  pages: Partial<Record<MergeSection, LoadedPage>>
): MergeCountsDto {
  return estimateCountsFromFacts(
    server,
    serverDecisionKeys,
    decisions,
    cellFactsOf(pages)
  );
}

/**
 * Server counts adjusted for every decision made since the server last
 * counted, by the API's own counting rules (`engine.counts`):
 * - A decision on a CONFLICT moves it from Remaining to Resolved, whatever it
 *   decides, and removing it moves it back.
 * - A decision on any other cell is an override only if its value differs from
 *   the auto value (`valuesEqual`), so "Take Mine" on a MINE_ONLY cell is not
 *   one. `serverDecisions` holds the decisions the server counted, so a counted
 *   decision that was changed (not added or removed) is seen too.
 *
 * `facts` is every cell the screen has loaded, not only the page on screen,
 * so a decision on a page the user has since left still counts. A decision
 * naming a cell that was never loaded, or that cannot take one, is not counted.
 *
 * Nothing here is sent to the server. Submit does not rely on it: it rebases
 * and takes the server's counts and checks before it sends.
 */
export function estimateCountsFromFacts(
  server: MergeCountsDto,
  serverDecisionKeys: CellKey[],
  decisions: Record<CellKey, MergeDecisionDto>,
  facts: Record<CellKey, CellFact>,
  serverDecisions: Record<CellKey, MergeDecisionDto> = {}
): MergeCountsDto {
  let resolvedDelta = 0;
  let remainingDelta = 0;
  let overridesDelta = 0;

  // Map Input_Name → adjustment deltas for ByInput.
  const byInputDeltas = new Map<
    string,
    { resolved: number; remaining: number; overrides: number }
  >();

  const serverDecisionKeySet = new Set(serverDecisionKeys);
  const keys = new Set<CellKey>([
    ...(Object.keys(decisions) as CellKey[]),
    ...serverDecisionKeys,
  ]);

  keys.forEach((key) => {
    const fact = facts[key];
    if (fact === undefined) {
      // Never loaded, or a cell that cannot take a decision.
      return;
    }
    const local = decisions[key];
    const counted = serverDecisionKeySet.has(key);

    let resolved = 0;
    let overrides = 0;
    if (fact.conflict) {
      resolved = (local ? 1 : 0) - (counted ? 1 : 0);
    } else {
      // A counted decision whose value is unknown is taken to be an override.
      const countedDecision = serverDecisions[key];
      const countedOverride = counted
        ? countedDecision
          ? isOverride(fact, countedDecision)
          : true
        : false;
      overrides =
        (local && isOverride(fact, local) ? 1 : 0) - (countedOverride ? 1 : 0);
    }
    if (resolved === 0 && overrides === 0) {
      return;
    }

    resolvedDelta += resolved;
    remainingDelta -= resolved;
    overridesDelta += overrides;

    // Adjust ByInput if Input_Name is present.
    if (fact.inputName !== undefined) {
      const delta = byInputDeltas.get(fact.inputName) || {
        resolved: 0,
        remaining: 0,
        overrides: 0,
      };
      delta.resolved += resolved;
      delta.remaining -= resolved;
      delta.overrides += overrides;
      byInputDeltas.set(fact.inputName, delta);
    }
  });

  // Build new counts object without mutating server.
  const result: MergeCountsDto = {
    TotalConflicts: server.TotalConflicts,
    Resolved: server.Resolved + resolvedDelta,
    Remaining: server.Remaining + remainingDelta,
    AutoResolved: server.AutoResolved,
    Overrides: server.Overrides + overridesDelta,
    ChangedUnderneath: server.ChangedUnderneath,
  };

  // Preserve optional fields if present.
  if (server.CurrentSubmissionID !== undefined) {
    result.CurrentSubmissionID = server.CurrentSubmissionID;
  }
  if (server.PreviousCurrentSubmissionID !== undefined) {
    result.PreviousCurrentSubmissionID = server.PreviousCurrentSubmissionID;
  }

  // Apply ByInput adjustments if present in server.
  if (server.ByInput && server.ByInput.length > 0) {
    result.ByInput = server.ByInput.map((entry) => {
      const delta = byInputDeltas.get(entry.Input_Name);
      if (!delta) {
        return entry;
      }
      return {
        ...entry,
        Resolved: entry.Resolved + delta.resolved,
        Remaining: entry.Remaining + delta.remaining,
        Overrides: entry.Overrides + delta.overrides,
      };
    });
  }

  return result;
}
